/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GatedSection } from "../../components/GatedSection";
import { UpgradeModal } from "../../features/partner/components/UpgradeModal";

// API services
import { getLatestRun, queueRun, getById } from "../../api/projects";
import { getStatus } from "../../api/customers";
import {
    listScenarios,
    listProfiles,
    createScenario as apiCreateScenario,
    deleteScenario as apiDeleteScenario,
    createProfile as apiCreateProfile,
    deleteProfile as apiDeleteProfile,
    updateProfile as apiUpdateProfile,
    queueScenarioRun,
    getComparison,
    Scenario,
    GenerationProfile,
    ScenarioCreatePayload,
    ComparisonData,
} from "../../api/scenarios";

// Custom hooks
import { useCustomerId } from "../../hooks/useCustomerId";
import { useImpactData } from "../../hooks/useImpactData";
import { useModelRunPoller } from "../../hooks/useModelRunPoller";
import { useResourceCheck } from "../../hooks/useResourceCheck";

// Utils
import { parseNumericInputs } from "../../utils/formHelpers";

// Types
import {
    ModelInputs,
    ProjectDetail as ProjectDetailType,
} from "../../features/projects/domain/types";
import { ModelResults } from "../../features/model/domain/types";
import { CustomerStatus } from "../../features/customers/domain/types";
import { RunPhase } from "../../features/model/components/RunningIndicator";

// Components
import { RunningIndicator } from "../../features/model/components/RunningIndicator";
import { ResultsViewer } from "../../features/model/components/ResultsViewer";
import {
  VersionStamp,
  StalenessBadge,
  RerunConfirmDialog,
} from "../../features/model-version";
import { PlanInfernoSection } from "../../features/inferno";
import { RunConfirmationModal } from "../../features/model/components/RunConfirmationModal";
import { ErrorModal } from "../../features/common/components/ErrorModal";
import BaselineComparisonCard from "../../features/baseline/components/BaselineComparisonCard";
import { Modal } from "../../features/common/components/Modal";
import { ModalButtons } from "../../features/common/components/ModalButtons";
import { ProjectDetailForm } from "../../features/projects/components/ProjectDetailForm";
import { ImpactMetrics } from "../../features/model/components/ImpactMetrics";
import { ImpactSummaryCard } from "../../features/model/components/ImpactSummaryCard";
import { ProjectImpactCard } from "../../features/projects/components/ProjectImpactCard";
import { RunModelButton } from "../../features/model/components/RunModelButton";
import { ProjectTagEditor } from "../../features/portfolio-dashboard/components/ProjectTagEditor";

import { ProjectSummaryCard } from "../../features/model/components/ProjectSummaryCard";

// New Impact & Lifetime Components
import { ImpactExplorer } from "../../features/impact/components/ImpactExplorer";
import { LifetimeProjectionChart } from "../../features/lifetime/components/LifetimeProjectionChart";
import { ImpactModeProvider, useImpactMode } from "../../features/impact/context/ImpactModeContext";
import { ImpactModeToggle } from "../../features/impact/components/ImpactModeToggle";
import { RangeModeToggle } from "../../features/impact/components/RangeModeToggle";
import { triggerProjectSweep } from "../../features/impact/api/sensitivity";
import { RangeDashboard } from "../../features/range-dashboard/RangeDashboard";
import { ProjectResultsView } from "../../features/project-results/ProjectResultsView";
import { TECH_LABELS } from "../../features/model/domain/definitions";

// Report generation
import { GenerateReportButton } from "../../features/report/GenerateReportButton";
import { GenerationChangeChart } from "../../features/model/components/GenerationChangeChart";
import { GenerationBreakdownTable } from "../../features/model/components/GenerationBreakdownTable";
import { GenerationProfileCard } from "../../features/projects/components/GenerationProfileCard";
import { InterconnectionCard } from "../../features/interconnection/components/InterconnectionCard";
import { GeneratorAttributionCard } from "../../features/spatial/components/GeneratorAttributionCard";
import { getLifetimeMultiplier } from "../../features/report/domain/reportDataExtractor";
import { ProfileComparisonData } from "../../features/report/domain/types";

// Scenario & Profile components
import { ScenarioSelector } from "../../features/projects/components/ScenarioSelector";
import { ScenarioCreateModal } from "../../features/projects/components/ScenarioCreateModal";
import { ProfileManager } from "../../features/projects/components/ProfileManager";
import { ScenarioComparisonTable } from "../../features/projects/components/ScenarioComparisonTable";

// Lifetime utilities
import { ImpactTotals } from "../../features/impact/domain/types";
import {
    LifetimeProjection,
    LifetimeProjectionConfig,
    DEFAULT_LIFETIME_CONFIG,
    generateLifetimeProjection,
} from "../../features/lifetime/domain/types";

/* ——— RENDER ——— */
export default function ProjectDetail() {
    // Add ref for scrolling to top
    const pageTopRef = useRef<HTMLDivElement>(null);
    const { projectId } = useParams<{ projectId: string }>();
    const [searchParams] = useSearchParams();
    const projectName = searchParams.get("name") || decodeURIComponent(projectId || "");

    // The Classic | Range view switcher (Range is now the default) lives in the
    // shared <ProjectResultsView/>; this page just supplies the two layouts.
    const { token, isPartner } = useAuth();
    const customerId = useCustomerId(token);
    const queryClient = useQueryClient();

    // —— State ——
    const [sweeping, setSweeping] = useState(false);
    // Epoch-ms deadline: while now < this, the latestRun query auto-polls so the
    // Range view notices the sweep finishing without a manual refresh. Covers
    // the full sweep (~1 min) plus margin; `status==="running"` keeps polling
    // past it if a sweep runs long.
    const [sweepWatchUntil, setSweepWatchUntil] = useState(0);
    const handleRunSweep = async () => {
        if (!projectId || !token) return;
        setSweeping(true);
        try {
            await triggerProjectSweep(projectId, token);
            // Bands land asynchronously via the worker; start the poll window so
            // the toggle picks up status running → complete on its own.
            setSweepWatchUntil(Date.now() + 90_000);
        } catch (err) {
            console.error("Failed to enqueue sensitivity sweep", err);
        } finally {
            setSweeping(false);
        }
    };
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [runPhase, setRunPhase] = useState<RunPhase>("idle");
    const [modelResults, setModelResults] = useState<ModelResults | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [rerunOpen, setRerunOpen] = useState(false);
    const [isEditDetailsOpen, setIsEditDetailsOpen] = useState(false);
    // Add state for the failed run message
    const [failedRunMessage, setFailedRunMessage] = useState<string | null>(null);
    // Track if this is an active polling run vs page load
    const [isActiveRun, setIsActiveRun] = useState(false);
    // Track if the current active run has reached queued/running at least once
    const [hasEnteredActive, setHasEnteredActive] = useState(false);
    // Scenario & profile state
    const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
    const [showScenarioCreate, setShowScenarioCreate] = useState(false);
    const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);

    // —— Custom Hooks ——
    const { data: impactData } = useImpactData(customerId);

    const { pollForResults, pollingRef } = useModelRunPoller(token, {
        onComplete: (results) => {
            setModelResults(results);
            setRunPhase("idle");
            setIsActiveRun(false);
            setFailedRunMessage(null); // Clear any failed message on success
            setHasEnteredActive(false);
            setComparisonRefreshKey((k) => k + 1);
            // Invalidate parent queries so dashboard/list show fresh data
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            queryClient.invalidateQueries({ queryKey: ["impactData"] });
            queryClient.invalidateQueries({ queryKey: ["partner-clients"] });
            queryClient.invalidateQueries({ queryKey: ["client-dashboard-data"] });
            queryClient.invalidateQueries({ queryKey: ["projectLatestRun", projectId] });
        },
        onError: (message) => {
            // Ignore transient "failed" reads that refer to the previous run
            // (i.e., before the new run ever reported queued/running)
            if (!hasEnteredActive) {
                return;
            }

            // Only show the modal if this was an active run
            if (isActiveRun) {
                setErrorMessage(message);
                setShowErrorModal(true);
            }

            // Set the persistent failure message for a confirmed active run failure
            setFailedRunMessage("The model run failed to complete. Please press \"Run Model\" to try again.");
            setRunPhase("idle");
            setIsActiveRun(false);
            setHasEnteredActive(false);
        },
        onStatusChange: (status) => {
            setRunPhase(status as RunPhase);
            if (status === "submitting" || status === "running") {
                setHasEnteredActive(true);
            }
        }
    });

    // —— Queries ——
    const {
        data: projectData,
    } = useQuery<ProjectDetailType>({
        queryKey: ["project", projectId],
        enabled: !!projectId && !!token,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        queryFn: async () => {
            if (!projectId || !token) throw new Error("Missing projectId or token");
            return await getById(projectId, token);
        },
    });

    const {
        data: customerStatus,
    } = useQuery<CustomerStatus | null>({
        queryKey: ["customerStatus", customerId],
        enabled: !!customerId && !!token,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        queryFn: async () => {
            if (!customerId || !token) return null;
            return await getStatus(customerId, token);
        },
    });

    const {
        data: latestRun,
        refetch: refetchLatestRun,
    } = useQuery<ModelResults | null>({
        queryKey: ["projectLatestRun", projectId],
        enabled: !!projectId && !!token,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        queryFn: async () => {
            try {
                return await getLatestRun(projectId!, token!);
            } catch (error: any) {
                if (error.status === 404) return null; // no prior runs
                throw error;
            }
        },
        // Auto-poll while a sensitivity sweep is in flight so the Range view
        // flips out of "Sweep running" on its own — the sweep runs async on the
        // worker (~1 min for 4 scenarios) and otherwise only updates on a manual
        // refresh. Two triggers: (a) the block already reports "running", and
        // (b) we just enqueued a sweep and are inside the watch window (bridges
        // the enqueue→first-row gap, where status is still "none"/prior for
        // ~40s, and covers re-runs where stale bands stay visible meanwhile).
        // React Query v5: the callback receives the query; data is at
        // query.state.data.
        refetchInterval: (query) => {
            const st = query.state.data?.scenarios?.status;
            if (st === "running") return 4000;
            if (sweepWatchUntil > Date.now()) return 4000;
            return false;
        },
    });

    // —— Scenario & Profile Queries ——
    const {
        data: scenariosData,
        refetch: refetchScenarios,
    } = useQuery({
        queryKey: ["scenarios", projectId],
        enabled: !!projectId && !!token,
        staleTime: 1000 * 60 * 5,
        queryFn: async () => {
            if (!projectId || !token) return { scenarios: [] as Scenario[] };
            return await listScenarios(projectId, token);
        },
    });

    const scenarios = scenariosData?.scenarios || [];

    // Auto-create a default scenario if none exist, so ProfileManager is always visible
    const [autoCreatingScenario, setAutoCreatingScenario] = useState(false);
    useEffect(() => {
        if (scenarios.length === 0 && projectId && token && scenariosData && !autoCreatingScenario) {
            setAutoCreatingScenario(true);
            apiCreateScenario(projectId, { scenario_name: 'Base Case' }, token)
                .then((created) => {
                    setSelectedScenarioId(created.scenario_id);
                    refetchScenarios();
                })
                .catch(() => {})
                .finally(() => setAutoCreatingScenario(false));
        }
    }, [scenarios.length, projectId, token, scenariosData, autoCreatingScenario]);

    // Auto-select default scenario
    useEffect(() => {
        if (scenarios.length > 0 && !selectedScenarioId) {
            const defaultScenario = scenarios.find((s: Scenario) => s.is_default);
            setSelectedScenarioId(defaultScenario?.scenario_id || scenarios[0].scenario_id);
        }
    }, [scenarios, selectedScenarioId]);

    // Auto-load comparison data for selected scenario (also re-fetches when runs finish)
    // If the auto-selected scenario has no profiles, fall back to the first scenario that does.
    const [comparisonRefreshKey, setComparisonRefreshKey] = useState(0);
    const [triedScenarioIds, setTriedScenarioIds] = useState<Set<string>>(new Set());
    useEffect(() => {
        if (!projectId || !token || !selectedScenarioId) return;
        getComparison(projectId, token, { scenario_id: selectedScenarioId })
            .then((data) => {
                if (data.profiles.length > 1) {
                    setComparisonData(data);
                    setTriedScenarioIds(new Set());
                } else {
                    setComparisonData(null);
                    // If this scenario has no comparison data and there are other scenarios, try the next one
                    const nextScenario = scenarios.find(
                        (s: Scenario) => s.scenario_id !== selectedScenarioId && !triedScenarioIds.has(s.scenario_id)
                    );
                    if (nextScenario) {
                        setTriedScenarioIds(prev => new Set(prev).add(selectedScenarioId));
                        setSelectedScenarioId(nextScenario.scenario_id);
                    }
                }
            })
            .catch(() => { /* no comparison data yet */ });
    }, [projectId, token, selectedScenarioId, comparisonRefreshKey]);

    const {
        data: profilesData,
        refetch: refetchProfiles,
    } = useQuery({
        queryKey: ["profiles", projectId],
        enabled: !!projectId && !!token,
        staleTime: 1000 * 60 * 5,
        queryFn: async () => {
            if (!projectId || !token) return { profiles: [] as GenerationProfile[] };
            return await listProfiles(projectId, token);
        },
    });

    const profiles = profilesData?.profiles || [];

    // —— Resource check ——
    const { hasResourcesForRun, resourceLimitMessage } = useResourceCheck(customerStatus || null);

    // —— Form State ——
    const [details, setDetails] = useState<ModelInputs>({
        customer_id: customerId || "",
        project_name: projectName,
        latitude: 40.1164,
        longitude: -88.2434,
        state: "IL",
        county: "Champaign",
        city: "Champaign",
        zip_code: "61820",
        timezone: "America/Chicago",
        project_nerc_code: "US-TRE",
        eia_ba_code: "ERCO",
        cod_date: "2025-07-01",
        decommision_date: "",
        nameplate_capacity: 17.5,
        poi_limit: 17.5,
        project_additional_load: 0.0,
        existing_project_flag: true,
        tech_type_id: "wind_onshore",
        ppa_flag: false,
        ppa_percentage: 1.0,
        ppa_capacity: 17.5,
        battery_flag: false,
        battery_capacity_mwh: 0.0,
        battery_charge_rate_mw: 0.0,
        battery_discharge_rate_mw: 0.0,
        battery_round_trip_eff: 1.0,
        battery_soc_min: 0.0,
        battery_chemistry: "lfp",
        battery_calendar_life_years: 20.0,
        battery_cycle_life: 6000,
        battery_augmentation_flag: false,
        battery_depth_of_discharge: 0.85,
        gen_hour_profile_mw: [],
    });

    const [editDetails, setEditDetails] = useState<ModelInputs>(details);
    // —— Effects ——
    // Update details when project data is loaded
    useEffect(() => {
        if (projectData) {
            setDetails(prev => ({ ...prev, ...projectData }));
        }
    }, [projectData]);

    // Update the project_name whenever the URL parameter changes
    useEffect(() => {
        setDetails(prev => ({ ...prev, project_name: projectName }));
    }, [projectName]);

    // Hydrate local UI from cached/latest run
    useEffect(() => {
        if (!latestRun) {
            if (!pollingRef.current) {
                setModelResults(null);
            }
            return;
        }

        if (latestRun.status === "completed") {
            setModelResults(latestRun);
            setRunPhase("idle");
            setFailedRunMessage(null); // Clear any failed run message
        } else if (latestRun.status === "queued" || latestRun.status === "running") {
            // Only start polling if not already polling (prevents duplicate loops)
            if (!pollingRef.current) {
                setRunPhase("running");
                setIsActiveRun(true);
                if (projectId) pollForResults(projectId);
            }
        } else if (latestRun.status === "failed") {
            // Do not re-assert the message if we're actively trying to start a run
            if (!isActiveRun && !pollingRef.current) {
                setFailedRunMessage("The most recent run for this project failed. Please press \"Run Model\" to try again.");
                setRunPhase("idle");
            }
        }
    }, [latestRun, projectId, pollForResults, isActiveRun, pollingRef]);

    // Add scroll to top effect
    useEffect(() => {
        // Scroll to top of page
        window.scrollTo(0, 0);
        // Alternative: scroll to the top ref element
        pageTopRef.current?.scrollIntoView({ behavior: "auto" });
    }, [projectId]); // Re-run when project ID changes

    // —— Derived Data ——
    const isDetailsComplete = useMemo(() => !!projectId, [projectId]);

    const projectImpact = useMemo(() => {
        if (projectId && impactData?.projectImpacts) {
            return impactData.projectImpacts[projectId];
        }
        return null;
    }, [projectId, impactData]);

    // Check if this is a future project (NOT an existing project)
    const isFutureProject = useMemo(() => {
        // If any source says it's an existing project, it's NOT a future project
        if (projectData?.existing_project_flag === true) return false;
        if (modelResults?.run_type === 'existing') return false;
        if (details.existing_project_flag === true) return false;
        // Only return true if we have confirmed it's a future project
        if (projectData?.existing_project_flag === false) return true;
        if (modelResults?.run_type === 'future') return true;
        // Default to false until we have data
        return false;
    }, [projectData?.existing_project_flag, details.existing_project_flag, modelResults?.run_type]);

    // Extract impact totals from model results for new components.
    // Backend emits Cradle_to_Grave_Analysis (full cradle-to-grave totals) and
    // Operational_Analysis (ops-only, cradle-to-grave minus infrastructure).
    // impactTotals here holds the full cradle-to-grave totals.
    const impactTotals: ImpactTotals | null = useMemo(() => {
        if (!modelResults?.summary_json?.Impacts?.Cradle_to_Grave_Analysis?.Total) return null;
        const total = modelResults.summary_json.Impacts.Cradle_to_Grave_Analysis.Total;
        const summaryJson = modelResults.summary_json;

        // Get generation from generation_metadata (most reliable source)
        let generationMwh = 0;
        if (summaryJson.generation_metadata?.actual_generation_gwh) {
            generationMwh = summaryJson.generation_metadata.actual_generation_gwh * 1000;
        } else if (summaryJson.benefit_frame?.renewable_contribution?.total_gwh) {
            generationMwh = summaryJson.benefit_frame.renewable_contribution.total_gwh * 1000;
        } else {
            // Fallback: estimate from capacity and typical capacity factor
            const capacity = typeof details.nameplate_capacity === 'number'
                ? details.nameplate_capacity
                : parseFloat(String(details.nameplate_capacity)) || 0;
            generationMwh = capacity * 8760 * 0.25;
        }

        // Extract avoided CO2e from benefit_frame or emissions (in tonnes, convert to kg)
        let co2eKg = 0;
        if (summaryJson.benefit_frame?.emissions_avoided?.Carbon) {
            co2eKg = summaryJson.benefit_frame.emissions_avoided.Carbon * 1000;
        } else if (summaryJson.Emissions?.Cradle_to_Grave_Analysis?.air) {
            const carbonEmission = summaryJson.Emissions.Cradle_to_Grave_Analysis.air.find(
                (e: { Name: string; Value: number }) => e.Name === 'Carbon'
            );
            if (carbonEmission) {
                co2eKg = carbonEmission.Value * 1000;
            }
        }

        return {
            dalys: total.DALYs || 0,
            pdfs: total.PDFs || 0,
            usd: total.USD || 0,
            co2eKg,
            generationMwh,
        };
    }, [modelResults, details.nameplate_capacity]);

    // Ops totals — from Operational_Analysis (ops-only, cradle-to-grave minus infrastructure)
    const opsTotals: ImpactTotals | null = useMemo(() => {
        if (!modelResults?.summary_json?.Impacts?.Operational_Analysis?.Total) return null;
        const opsTotal = modelResults.summary_json.Impacts.Operational_Analysis.Total;
        return {
            dalys: opsTotal.DALYs || 0,
            pdfs: opsTotal.PDFs || 0,
            usd: opsTotal.USD || 0,
            co2eKg: impactTotals?.co2eKg || 0,
            generationMwh: impactTotals?.generationMwh || 0,
        };
    }, [modelResults, impactTotals]);

    // Raw totals objects for ImpactModeProvider
    const netTotalsRaw = useMemo(() => {
        if (!modelResults?.summary_json?.Impacts?.Cradle_to_Grave_Analysis?.Total) return null;
        return modelResults.summary_json.Impacts.Cradle_to_Grave_Analysis.Total;
    }, [modelResults]);

    const opsTotalsRaw = useMemo(() => {
        if (!modelResults?.summary_json?.Impacts?.Operational_Analysis?.Total) return null;
        return modelResults.summary_json.Impacts.Operational_Analysis.Total;
    }, [modelResults]);

    // Generate lifetime projection (for FUTURE projects only - existing projects show actual data)
    const lifetimeProjection: LifetimeProjection | null = useMemo(() => {
        if (!isFutureProject || !modelResults || !impactTotals) return null;

        const techType = details.tech_type_id || 'solar_ground';
        const techConfig = DEFAULT_LIFETIME_CONFIG[techType] || DEFAULT_LIFETIME_CONFIG.solar_ground;

        const config: LifetimeProjectionConfig = {
            projectLifetimeYears: techConfig.projectLifetimeYears || 25,
            socialDiscountRate: 0.03, // TODO: Get from customer settings
            annualDegradationPct: techConfig.annualDegradationPct || 0.005,
            degradationModel: techConfig.degradationModel || 'exponential',
            includeConstructionImpacts: false,
            includeDecommissioningImpacts: false,
        };

        const baseYearMwh = impactTotals.generationMwh;
        if (baseYearMwh <= 0) return null;

        const impactRates = {
            dalysPerMwh: impactTotals.dalys / baseYearMwh,
            pdfsPerMwh: impactTotals.pdfs / baseYearMwh,
            usdPerMwh: impactTotals.usd / baseYearMwh,
            co2eKgPerMwh: impactTotals.co2eKg / baseYearMwh,
        };

        // Get COD year from project data
        const codYear = details.cod_date
            ? new Date(details.cod_date).getFullYear()
            : new Date().getFullYear() + 1;

        const yearlyProjections = generateLifetimeProjection(baseYearMwh, impactRates, config, codYear);

        // Calculate lifetime totals
        const lifetimeGenerationGwh = yearlyProjections.reduce((sum, yr) => sum + yr.projectedGenerationMwh, 0) / 1000;
        const lifetimeAvoidedCo2eTonnes = yearlyProjections.reduce((sum, yr) => sum + yr.avoidedCo2eKg, 0) / 1000;
        const lifetimeAvoidedDalys = yearlyProjections.reduce((sum, yr) => sum + yr.avoidedDalys, 0);
        const lifetimeAvoidedPdfs = yearlyProjections.reduce((sum, yr) => sum + yr.avoidedPdfs, 0);
        const lifetimeEconomicNominalUsd = yearlyProjections.reduce((sum, yr) => sum + yr.economicValueNominalUsd, 0);
        const lifetimeEconomicNpvUsd = yearlyProjections.reduce((sum, yr) => sum + yr.economicValueNpvUsd, 0);

        return {
            projectId: projectId || '',
            config,
            codYear,
            baseYearGenerationMwh: baseYearMwh,
            baseYearCapacityFactor: baseYearMwh / ((typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 1) * 8760),
            impactRates,
            yearlyProjections,
            lifetimeGenerationGwh,
            lifetimeAvoidedCo2eTonnes,
            lifetimeAvoidedDalys,
            lifetimeAvoidedPdfs,
            lifetimeEconomicNominalUsd,
            lifetimeEconomicNpvUsd,
            infrastructureCo2eTonnes: null,
            netLifetimeCo2eTonnes: lifetimeAvoidedCo2eTonnes,
            calculatedAt: new Date().toISOString(),
            modelVersion: '1.0',
        };
    }, [isFutureProject, modelResults, impactTotals, details, projectId]);

    // Transform comparison data for report (ProfileComparisonData format)
    const profileComparisonForReport: ProfileComparisonData | undefined = useMemo(() => {
        if (!comparisonData || comparisonData.profiles.length < 2) return undefined;
        return {
            profiles: comparisonData.profiles
                .filter(p => p.status === "completed")
                .map(p => ({
                    label: p.profile_label,
                    generationMwh: p.generation_mwh || 0,
                    dalys: p.dalys || 0,
                    pdfs: p.pdfs || 0,
                    usd: p.usd || 0,
                    co2eKg: p.co2e_kg || 0,
                    healthBreakdown: p.health_breakdown?.map(e => ({ category: e.category, value: e.value || 0, usd: e.usd || 0 })),
                    biodiversityBreakdown: p.biodiversity_breakdown?.map(e => ({ category: e.category, value: e.value || 0, usd: e.usd || 0 })),
                    economicBreakdown: p.economic_breakdown?.map(e => ({ category: e.category, value: e.value || 0, usd: e.usd || 0 })),
                })),
        };
    }, [comparisonData]);

    // —— Handlers ——
    const handleRunModel = () => {
        if (!isDetailsComplete || !projectId) return;

        // First clear the message
        setFailedRunMessage(null);

        // Then show the confirmation modal in the next render cycle
        requestAnimationFrame(() => {
            setShowConfirmModal(true);
        });
    };

    const runModelNow = async () => {
        setShowConfirmModal(false);
        setModelResults(null);
        setRunPhase("submitting");
        setFailedRunMessage(null); // Clear any failed run message
        setIsActiveRun(true); // Mark this as an active run
        setHasEnteredActive(false);

        try {
            await queueRun(projectId!, token!);

            queryClient.setQueryData<ModelResults | null>(["projectLatestRun", projectId], {
                status: "queued",
                summary_json: {},
            } as ModelResults);

            setRunPhase("running");
            pollForResults(projectId!);
        } catch (err: any) {
            setErrorMessage(err.message ?? String(err));
            setShowErrorModal(true);
            setRunPhase("idle");
            setIsActiveRun(false);
            setHasEnteredActive(false);
            setFailedRunMessage("Failed to start the model run. Please try again.");
        }
    };

    const openDetailsModal = () => {
        setEditDetails(details);
        setIsEditDetailsOpen(true);
    };

    const handleDetailsSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setDetails(parseNumericInputs(editDetails));
        setIsEditDetailsOpen(false);
    };

    // —— Scenario Handlers ——
    const handleCreateScenario = async (payload: ScenarioCreatePayload) => {
        if (!projectId || !token) return;
        try {
            const created = await apiCreateScenario(projectId, payload, token);
            setShowScenarioCreate(false);
            setSelectedScenarioId(created.scenario_id);
            refetchScenarios();
        } catch (err: any) {
            setErrorMessage(err.message ?? String(err));
            setShowErrorModal(true);
        }
    };

    const handleDeleteScenario = async (scenarioId: string) => {
        if (!projectId || !token) return;
        if (!window.confirm("Delete this scenario and all its runs?")) return;
        try {
            await apiDeleteScenario(projectId, scenarioId, token);
            if (selectedScenarioId === scenarioId) {
                const defaultS = scenarios.find((s: Scenario) => s.is_default);
                setSelectedScenarioId(defaultS?.scenario_id || null);
            }
            refetchScenarios();
        } catch (err: any) {
            setErrorMessage(err.message ?? String(err));
            setShowErrorModal(true);
        }
    };

    // —— Profile Handlers ——
    const handleUploadProfile = async (
        label: string,
        confidenceLevel: number | null,
        data: Array<{ timestamp: string; value: number }>,
        isPrimary: boolean,
    ) => {
        if (!projectId || !token) return;
        try {
            await apiCreateProfile(projectId, {
                profile_label: label,
                confidence_level: confidenceLevel ?? undefined,
                scenario_id: selectedScenarioId ?? undefined,
                gen_hour_profile_mw: data,
                is_primary: isPrimary,
            }, token);
            refetchProfiles();
        } catch (err: any) {
            setErrorMessage(err.message ?? String(err));
            setShowErrorModal(true);
        }
    };

    const handleDeleteProfile = async (profileId: string) => {
        if (!projectId || !token) return;
        try {
            await apiDeleteProfile(projectId, profileId, token);
            refetchProfiles();
        } catch (err: any) {
            setErrorMessage(err.message ?? String(err));
            setShowErrorModal(true);
        }
    };

    const handleSetPrimaryProfile = async (profileId: string) => {
        if (!projectId || !token) return;
        try {
            await apiUpdateProfile(projectId, profileId, { is_primary: true }, token);
            refetchProfiles();
        } catch (err: any) {
            setErrorMessage(err.message ?? String(err));
            setShowErrorModal(true);
        }
    };

    // —— Run All Profiles ——
    const handleRunAllProfiles = async () => {
        if (!projectId || !token || !selectedScenarioId) return;
        // Don't clear modelResults — keep existing P50 results visible during multi-profile run
        setRunPhase("submitting");
        setFailedRunMessage(null);
        setIsActiveRun(true);
        setHasEnteredActive(false);

        try {
            const result = await queueScenarioRun(projectId, token, {
                scenario_id: selectedScenarioId,
                run_all_profiles: true,
            });

            if ("run_group_id" in result) {
                // Multi-profile run — start polling comparison
                setRunPhase("running");
                const pollComparison = async () => {
                    const data = await getComparison(projectId, token, {
                        run_group_id: result.run_group_id,
                    });
                    setComparisonData(data);
                    const allDone = data.profiles.every(
                        (p) => p.status === "completed" || p.status === "failed",
                    );
                    if (allDone) {
                        setRunPhase("idle");
                        setIsActiveRun(false);
                        refetchScenarios();
                        refetchLatestRun();
                        setComparisonRefreshKey((k) => k + 1);
                    } else {
                        setTimeout(pollComparison, 5000);
                    }
                };
                setTimeout(pollComparison, 3000);
            } else {
                // Single run
                setRunPhase("running");
                pollForResults(projectId);
            }
        } catch (err: any) {
            setErrorMessage(err.message ?? String(err));
            setShowErrorModal(true);
            setRunPhase("idle");
            setIsActiveRun(false);
        }
    };

    // ————————————————————————————————————————————————————————————————
    // Shared results view — the Range dashboard is the DEFAULT; Classic is opt-in.
    // The must-port controls below are defined once and referenced by BOTH the
    // Range and Classic layouts (only one view mounts at a time), so nothing is
    // duplicated and nothing is lost when Range is the default.
    // ————————————————————————————————————————————————————————————————
    const canRunModel = isDetailsComplete && runPhase === "idle" && hasResourcesForRun;

    // Run / Edit / Report actions — surfaced in the Range dashboard header so the
    // Range view keeps first-class Run/Edit/Report parity with the Classic card.
    const rangeHeaderActions = (
        <>
            <button
                onClick={openDetailsModal}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-text-secondary hover:text-text-primary hover:bg-surface-600 transition-colors"
            >
                Edit project
            </button>
            <button
                onClick={handleRunModel}
                disabled={!canRunModel}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-quantum-400 border-quantum-500/40 bg-quantum-500/10 hover:bg-quantum-500/20"
            >
                Run model
            </button>
            {modelResults && impactTotals && (
                <GenerateReportButton
                    projectName={projectName}
                    techType={details.tech_type_id || 'solar_ground'}
                    capacityMw={typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
                    location={`${details.latitude}, ${details.longitude}`}
                    analysisYear={modelResults.year || new Date().getFullYear()}
                    isFutureProject={isFutureProject}
                    codDate={typeof details.cod_date === 'string' ? details.cod_date : undefined}
                    customerName={(projectData as any)?.customer_name}
                    county={typeof details.county === 'string' ? details.county : undefined}
                    existingProjectFlag={!isFutureProject}
                    modelResults={modelResults}
                    impactTotals={impactTotals}
                    lifetimeProjection={lifetimeProjection}
                    latitude={typeof details.latitude === 'number' ? details.latitude : parseFloat(String(details.latitude)) || undefined}
                    longitude={typeof details.longitude === 'number' ? details.longitude : parseFloat(String(details.longitude)) || undefined}
                    eiaBaCode={typeof details.eia_ba_code === 'string' ? details.eia_ba_code : undefined}
                    nercCode={typeof details.project_nerc_code === 'string' ? details.project_nerc_code : undefined}
                    state={typeof details.state === 'string' ? details.state : undefined}
                    profileComparison={profileComparisonForReport}
                />
            )}
        </>
    );

    // Profiles & Scenarios management (collapsible) — advanced control.
    const scenariosPanel = scenarios.length > 0 ? (
        <details className="mt-4 rounded-lg bg-surface-700 border border-border">
            <summary className="px-4 py-3 cursor-pointer text-body-sm font-medium text-text-secondary hover:text-text-primary transition-colors select-none">
                Profiles & Scenarios
                {profiles.length > 0 && <span className="ml-2 text-text-muted">({profiles.length} profile{profiles.length !== 1 ? 's' : ''})</span>}
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-border/50 pt-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <ScenarioSelector
                        scenarios={scenarios}
                        selectedScenarioId={selectedScenarioId}
                        onSelect={setSelectedScenarioId}
                        onCreate={() => setShowScenarioCreate(true)}
                        onClone={(sid) => {
                            const s = scenarios.find((sc: Scenario) => sc.scenario_id === sid);
                            if (s) handleCreateScenario({
                                scenario_name: `${s.scenario_name} (copy)`,
                                clone_from_scenario_id: sid,
                            });
                        }}
                        onDelete={handleDeleteScenario}
                    />
                    {profiles.length > 1 && (
                        <button
                            onClick={handleRunAllProfiles}
                            disabled={runPhase !== "idle"}
                            className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-quantum-400 border-quantum-500/40 bg-quantum-500/10 hover:bg-quantum-500/20"
                        >
                            Run All Profiles ({profiles.length})
                        </button>
                    )}
                </div>
                <ProfileManager
                    profiles={profiles}
                    onUpload={handleUploadProfile}
                    onDelete={handleDeleteProfile}
                    onSetPrimary={handleSetPrimaryProfile}
                    samProfileFlag={projectData?.sam_profile_flag}
                    generationMetadata={modelResults?.summary_json?.generation_metadata}
                />
            </div>
        </details>
    ) : null;

    const comparisonTable = comparisonData && comparisonData.profiles.length > 1 ? (
        <div className="mt-4">
            <ScenarioComparisonTable
                profiles={comparisonData.profiles}
                scenarioName={scenarios.find((s: Scenario) => s.scenario_id === selectedScenarioId)?.scenario_name}
            />
        </div>
    ) : null;

    // The hourly ImpactExplorer — the "explore hourly" view — is a must-have in
    // both layouts, partner-gated exactly as in Classic.
    const hourlyExplorer =
        (modelResults || (comparisonData && comparisonData.profiles.length > 1)) && projectId ? (
            isPartner ? (
                <div className="mt-6">
                    <GatedSection title="Time Series Data" onUpgrade={() => setShowUpgradeModal(true)}>
                        <ImpactExplorer
                            projectId={projectId}
                            isFutureProject={isFutureProject}
                            comparisonProfiles={comparisonData && comparisonData.profiles.length > 1 ? comparisonData.profiles : undefined}
                        />
                    </GatedSection>
                </div>
            ) : (
                <div className="mt-6">
                    <ImpactExplorer
                        projectId={projectId}
                        isFutureProject={isFutureProject}
                        comparisonProfiles={comparisonData && comparisonData.profiles.length > 1 ? comparisonData.profiles : undefined}
                    />
                </div>
            )
        ) : null;

    // Must-port extras shown BELOW the Range dashboard when Range is active.
    const rangeExtras = (
        <>
            {scenariosPanel}
            {comparisonTable}
            {hourlyExplorer}
        </>
    );

    const rangeContent = modelResults && impactTotals ? (
        <RangeDashboard
            projectName={projectName}
            techLabel={TECH_LABELS[details.tech_type_id || "solar_ground"] || (details.tech_type_id || "").replace(/_/g, " ")}
            capacity={typeof details.nameplate_capacity === "number" ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
            baCode={typeof details.eia_ba_code === "string" ? details.eia_ba_code : undefined}
            nercCode={typeof details.project_nerc_code === "string" ? details.project_nerc_code : undefined}
            analysisYear={modelResults.year || new Date().getFullYear()}
            isExisting={projectData?.existing_project_flag}
            impactTotals={impactTotals}
            opsTotals={opsTotals}
            generationMwh={impactTotals?.generationMwh}
            generationMeta={modelResults.summary_json?.generation_metadata as any}
            generationData={modelResults.summary_json?.Generation}
            avoidedCo2eTonnes={impactTotals?.co2eKg ? impactTotals.co2eKg / 1000 : undefined}
            scenarios={modelResults.scenarios}
            summaryJson={modelResults.summary_json}
            onRunSweep={handleRunSweep}
            sweeping={sweeping}
            projectId={projectId}
            projectLatitude={typeof projectData?.latitude === "number" ? projectData.latitude : undefined}
            projectLongitude={typeof projectData?.longitude === "number" ? projectData.longitude : undefined}
            runStatus={modelResults.status}
            lifetimeProjection={lifetimeProjection}
            headerActions={rangeHeaderActions}
        />
    ) : null;

    return (
        <div className="min-h-screen overflow-y-auto pt-0 pb-8">
            {/* Add ref to the top of your component */}
            <div ref={pageTopRef}></div>
            
            <ImpactModeProvider netTotals={netTotalsRaw ?? null} opsTotals={opsTotalsRaw ?? null}>
            {/* Model-version staleness — amber "re-run to update" banner */}
            {modelResults?.status === "completed" && modelResults.is_outdated && (
                <div className="mb-4">
                    <StalenessBadge
                        isOutdated={modelResults.is_outdated}
                        currentModelVersion={modelResults.current_model_version}
                        onRerun={() => setRerunOpen(true)}
                        disabled={runPhase !== "idle"}
                    />
                </div>
            )}

            <ProjectResultsView
                hasResults={!!(modelResults && impactTotals)}
                rangeContent={rangeContent}
                rangeExtras={rangeExtras}
                classicContent={
            <>
            {/* Unified Project Summary Card */}
            <ProjectSummaryCard
                scenarios={modelResults?.scenarios}
                projectName={projectName}
                techType={details.tech_type_id || 'solar_ground'}
                capacity={typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
                location={`${details.latitude}, ${details.longitude}`}
                analysisYear={modelResults?.year || new Date().getFullYear()}
                impactTotals={impactTotals}
                opsTotals={opsTotals}
                lifetimeProjection={lifetimeProjection}
                onEditDetails={openDetailsModal}
                onRunModel={handleRunModel}
                canRun={isDetailsComplete && runPhase === "idle" && hasResourcesForRun}
                lensToggle={modelResults ? (
                    <div className="flex items-center gap-2">
                        <ImpactModeToggle />
                        <RangeModeToggle
                            status={modelResults.scenarios?.status}
                            onRunSweep={handleRunSweep}
                            sweeping={sweeping}
                        />
                    </div>
                ) : undefined}
                baCode={details.eia_ba_code || undefined}
                generationMwh={impactTotals?.generationMwh}
                isExisting={projectData?.existing_project_flag}
                generationData={modelResults?.summary_json?.Generation}
                avoidedCo2eTonnes={impactTotals?.co2eKg ? impactTotals.co2eKg / 1000 : undefined}
                backTo={{ to: "/app/dashboard", label: "Dashboard" }}
                reportButtons={modelResults && impactTotals ? (
                    <GenerateReportButton
                        projectName={projectName}
                        techType={details.tech_type_id || 'solar_ground'}
                        capacityMw={typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
                        location={`${details.latitude}, ${details.longitude}`}
                        analysisYear={modelResults.year || new Date().getFullYear()}
                        isFutureProject={isFutureProject}
                        codDate={typeof details.cod_date === 'string' ? details.cod_date : undefined}
                        customerName={(projectData as any)?.customer_name}
                        county={typeof details.county === 'string' ? details.county : undefined}
                        existingProjectFlag={!isFutureProject}
                        modelResults={modelResults}
                        impactTotals={impactTotals}
                        lifetimeProjection={lifetimeProjection}
                        latitude={typeof details.latitude === 'number' ? details.latitude : parseFloat(String(details.latitude)) || undefined}
                        longitude={typeof details.longitude === 'number' ? details.longitude : parseFloat(String(details.longitude)) || undefined}
                        eiaBaCode={typeof details.eia_ba_code === 'string' ? details.eia_ba_code : undefined}
                        nercCode={typeof details.project_nerc_code === 'string' ? details.project_nerc_code : undefined}
                        state={typeof details.state === 'string' ? details.state : undefined}
                        profileComparison={profileComparisonForReport}
                    />
                ) : undefined}
            />

            {/* Persistent, subtle "computed on {version} · {date}" stamp */}
            {modelResults?.status === "completed" && modelResults.result_model_version && (
                <div className="mt-2">
                    <VersionStamp
                        resultModelVersion={modelResults.result_model_version}
                        date={modelResults.run_date || modelResults.updated_at || modelResults.created_at}
                    />
                </div>
            )}

            {/* Project Tags */}
            {projectId && (
                <div className="mt-4">
                    <ProjectTagEditor projectId={projectId} />
                </div>
            )}

            {/* Profiles & Scenarios + comparison — shared with the Range view. */}
            {scenariosPanel}
            {comparisonTable}

            {/* Report button is now in the ProjectSummaryCard header */}

            {/* Failed run message */}
            {failedRunMessage && (
                <div className="mt-4 rounded-lg bg-health-500/10 p-4 border border-health-500/30">
                    <p className="text-sm text-health-400">{failedRunMessage}</p>
                </div>
            )}

            {/* Empty state */}
            {!modelResults && runPhase === "idle" && !failedRunMessage && (
                <div className="mt-4 rounded-lg bg-surface-700 p-5 border border-border">
                    <p className="text-sm text-text-secondary">
                        <span className="font-medium text-text-primary">No model runs yet.</span> Click Run above to analyze this project.
                    </p>
                </div>
            )}

            {/* Indicators and Results */}
            <RunningIndicator phase={runPhase} />

            {/* Impact Metrics with Year 1 / Lifetime toggle — gated for partners */}
            {modelResults && (
                isPartner ? (
                    <GatedSection title="Impact Analysis" onUpgrade={() => setShowUpgradeModal(true)}>
                        <ImpactMetrics
                            modelResults={modelResults}
                            projectName={projectName}
                            lifetimeProjection={lifetimeProjection}
                            scenarios={modelResults.scenarios}
                        />
                    </GatedSection>
                ) : (
                    <ImpactMetrics
                        modelResults={modelResults}
                        projectName={projectName}
                        lifetimeProjection={lifetimeProjection}
                        scenarios={modelResults.scenarios}
                    />
                )
            )}

            {/* Grid Baseline Comparison — show after impact metrics */}
            {modelResults && impactTotals && impactTotals.generationMwh > 0 && (
                <BaselineComparisonCard
                    baCode={typeof details.eia_ba_code === 'string' ? details.eia_ba_code : null}
                    projectDalysPerMwh={impactTotals.dalys / impactTotals.generationMwh}
                    projectPdfsPerMwh={impactTotals.pdfs / impactTotals.generationMwh}
                    projectUsdPerMwh={impactTotals.usd / impactTotals.generationMwh}
                    projectGenerationMwh={impactTotals.generationMwh}
                />
            )}

            {/* Lifetime Projection Chart — future projects only, gated for partners */}
            {lifetimeProjection && (
                isPartner ? (
                    <GatedSection title="Lifetime Projection" onUpgrade={() => setShowUpgradeModal(true)}>
                        <LifetimeProjectionChart projection={lifetimeProjection} />
                    </GatedSection>
                ) : (
                    <LifetimeProjectionChart projection={lifetimeProjection} />
                )
            )}

            {/* Generation Profile — provenance + daily shape. Generation is
                the primary results driver, so it gets a first-class card. */}
            {projectId && modelResults?.status === "completed" && (
                <GenerationProfileCard
                    projectId={projectId}
                    generationMeta={modelResults?.summary_json?.generation_metadata as any}
                    runStatus={modelResults?.status}
                />
            )}

            {/* Generation Displacement Chart */}
            {modelResults?.summary_json?.Generation && (
                <GenerationChangeChart generationData={modelResults.summary_json.Generation} profileLabel={modelResults.profile_label} />
            )}

            {/* Per-fuel generation table (Historical / Difference / Interconnection) */}
            {modelResults?.summary_json?.Generation && (
                <GenerationBreakdownTable
                    generationData={modelResults.summary_json.Generation}
                    interconnectionBreakdown={modelResults.summary_json.interconnection_breakdown}
                    baCode={typeof details.eia_ba_code === "string" ? details.eia_ba_code : undefined}
                    profileLabel={modelResults.profile_label}
                />
            )}

            {/* Cross-Border Impact */}
            {modelResults?.summary_json?.interconnection_breakdown &&
             modelResults.summary_json.interconnection_breakdown.neighbors &&
             modelResults.summary_json.interconnection_breakdown.neighbors.length > 0 && (
                <InterconnectionCard
                    breakdown={modelResults.summary_json.interconnection_breakdown as any}
                    totalGenerationGwh={impactTotals?.generationMwh ? impactTotals.generationMwh / 1000 : 0}
                />
            )}

            {/* Generator Attribution — where your impact lands */}
            {modelResults?.summary_json?.generator_attribution &&
             modelResults.summary_json.generator_attribution.n_generators > 0 && (
                <GeneratorAttributionCard
                    attribution={modelResults.summary_json.generator_attribution}
                    congestion={modelResults.summary_json.congestion_metadata ?? undefined}
                    projectLatitude={typeof projectData?.latitude === 'number' ? projectData.latitude : undefined}
                    projectLongitude={typeof projectData?.longitude === 'number' ? projectData.longitude : undefined}
                    projectId={projectId}
                    projectName={details?.project_name || projectName}
                />
            )}

            {/* Impact Explorer (hourly) — shared with the Range view, gated for partners. */}
            {hourlyExplorer}

            {/* Show impact data from the hook if available and no model results */}
            {!modelResults && projectImpact && (
                <ProjectImpactCard impact={projectImpact} />
            )}

            {/* Results Viewer — gated for partners */}
            {modelResults && (
                isPartner ? (
                    <GatedSection title="Detailed Results" onUpgrade={() => setShowUpgradeModal(true)}>
                        <ResultsViewer
                            modelResults={modelResults}
                            projectName={projectName}
                            lifetimeMultiplier={getLifetimeMultiplier(lifetimeProjection)}
                            lifetimeYears={lifetimeProjection?.config.projectLifetimeYears}
                            scenarios={modelResults.scenarios}
                        />
                    </GatedSection>
                ) : (
                    <ResultsViewer
                        modelResults={modelResults}
                        projectName={projectName}
                        lifetimeMultiplier={getLifetimeMultiplier(lifetimeProjection)}
                        lifetimeYears={lifetimeProjection?.config.projectLifetimeYears}
                        scenarios={modelResults.scenarios}
                    />
                )
            )}

            {/* Plan Inferno — feature-flagged via REACT_APP_ENABLE_INFERNO. */}
            {modelResults?.id && projectId && (
                <PlanInfernoSection projectId={projectId} runId={modelResults.id} />
            )}
            </>
                }
            />
            </ImpactModeProvider>


            {/* Modals */}
            <RunConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={runModelNow}
                customerStatus={customerStatus || null}
                projectCapacity={details.nameplate_capacity}
            />

            <ErrorModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                message={errorMessage}
            />

            {/* Re-run on latest model (version staleness) */}
            <RerunConfirmDialog
                isOpen={rerunOpen}
                onClose={() => setRerunOpen(false)}
                changelog={modelResults?.changelog}
                resultModelVersion={modelResults?.result_model_version}
                currentModelVersion={modelResults?.current_model_version}
                onConfirm={runModelNow}
            />

            {/* Edit Details Modal */}
            {isEditDetailsOpen && (
                <Modal title="Edit Project Details" onClose={() => setIsEditDetailsOpen(false)}>
                    <form onSubmit={handleDetailsSubmit} className="flex flex-col h-[75vh]">
                        <ProjectDetailForm
                            details={editDetails}
                            onChange={(name, value) => setEditDetails(prev => ({ ...prev, [name]: value }))}
                        />
                        <ModalButtons onCancel={() => setIsEditDetailsOpen(false)} />
                    </form>
                </Modal>
            )}

            {/* Scenario Create Modal */}
            <ScenarioCreateModal
                isOpen={showScenarioCreate}
                onClose={() => setShowScenarioCreate(false)}
                onSubmit={handleCreateScenario}
                existingScenarios={scenarios}
            />

            {/* Partner Upgrade Modal */}
            <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                projectName={projectName}
                projectLocation={`${details.latitude}, ${details.longitude}`}
                projectCapacity={typeof details.nameplate_capacity === 'number' ? details.nameplate_capacity : parseFloat(String(details.nameplate_capacity)) || 0}
            />
        </div>
    );
}